import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Link as RouterLink } from 'react-router-dom';
import { DriverDashboardSummary } from '../types';
import { fetchDriverDashboardSummary } from '../services/driverWorkspaceService';
import { StatCardsSkeleton, HeroBannerSkeleton, CardRowSkeleton } from '../components/UI/PageSkeleton';

const StatCard = ({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  detail?: string;
}) => (
  <Paper variant="outlined" sx={{ p: 2.25, height: '100%', borderColor: 'rgba(7,24,45,0.08)' }}>
    <Stack spacing={1}>
      <Box sx={{ color: '#b98500' }}>{icon}</Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4" fontWeight={900}>{value}</Typography>
      {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
    </Stack>
  </Paper>
);

const DriverHome: React.FC = () => {
  const [summary, setSummary] = useState<DriverDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverDashboardSummary()
      .then(setSummary)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 1180, mx: 'auto' }}>
        <HeroBannerSkeleton />
        <StatCardsSkeleton count={4} />
        <CardRowSkeleton cols={2} />
        <CardRowSkeleton cols={2} />
      </Stack>
    );
  }

  if (!summary) {
    return (
      <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={700} mb={1}>Unable to load your workspace</Typography>
        <Typography color="text.secondary" mb={2}>There was a problem fetching your data. Please refresh the page.</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
      </Paper>
    );
  }

  const remaining = summary.entitlements.monthlyLeadLimit === null
    ? 'Unlimited'
    : summary.entitlements.remaining ?? 0;
  const planRemaining = summary.entitlements.planRemaining ?? (
    summary.entitlements.monthlyLeadLimit === null ? null : Math.max((summary.entitlements.monthlyLeadLimit || 0) - summary.entitlements.used, 0)
  );
  const walletCredits = summary.entitlements.walletCredits || 0;
  const quotaPercent = summary.entitlements.monthlyLeadLimit
    ? Math.min((summary.entitlements.used / summary.entitlements.monthlyLeadLimit) * 100, 100)
    : 0;
  const onboardingSteps: Array<[string, boolean]> = [
    ['Profile phone', summary.onboarding.profileComplete],
    ['Route preferences', summary.onboarding.preferencesComplete],
    ['Push notifications', summary.onboarding.notificationsEnabled],
    ['First unlock', summary.onboarding.firstLeadUnlocked],
  ];

  return (
    <Stack spacing={3} sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          bgcolor: '#061b31',
          color: 'white',
          background: 'linear-gradient(135deg,#061b31,#0b3156)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
          <Box>
            <Chip label={summary.profile.isOnline ? 'Online for rides' : 'Offline'} color={summary.profile.isOnline ? 'success' : 'default'} size="small" sx={{ mb: 1, bgcolor: summary.profile.isOnline ? undefined : 'rgba(255,255,255,0.12)', color: summary.profile.isOnline ? undefined : 'white' }} />
            <Typography variant="h4" fontWeight={900}>Welcome back, {summary.profile.name}</Typography>
            <Typography color="rgba(255,255,255,0.72)" mt={0.5}>
              {summary.stats.todayRides} new rides today. {remaining} unlocks available.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button component={RouterLink} to={summary.nextBestAction.path} variant="contained" color="secondary">
              {summary.nextBestAction.label}
            </Button>
            <Button component={RouterLink} to="/app/rides" variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.35)' }}>
              Browse rides
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard icon={<DirectionsCarIcon />} label="Active rides" value={summary.stats.activeRides} detail={`${summary.stats.todayRides} today`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard icon={<AccountBalanceWalletIcon />} label="Credits left" value={remaining} detail={summary.entitlements.planName} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard icon={<BookmarkIcon />} label="Saved rides" value={summary.stats.savedLeads} detail={`${summary.stats.hiddenLeads} hidden`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard icon={<EmojiEventsIcon />} label="Rides won" value={summary.stats.ridesWon} detail={`${summary.stats.totalUnlocked} unlocked`} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  {summary.entitlements.monthlyLeadLimit === 0 ? 'Wallet credits' : 'Monthly unlock usage'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summary.entitlements.monthlyLeadLimit === 0
                    ? 'Your free plan has no monthly unlock quota'
                    : `${summary.entitlements.used} used this month`}
                </Typography>
              </Box>
              <Button component={RouterLink} to="/wallet" size="small">Wallet</Button>
            </Box>
            {summary.entitlements.monthlyLeadLimit === null ? (
              <Chip label="Unlimited plan" color="success" />
            ) : summary.entitlements.monthlyLeadLimit === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Unlock rides with your wallet balance: {walletCredits} credit{walletCredits === 1 ? '' : 's'} available.
              </Typography>
            ) : (
              <Stack spacing={1}>
                <LinearProgress variant="determinate" value={quotaPercent} sx={{ height: 10, borderRadius: 5 }} color={quotaPercent > 85 ? 'warning' : 'primary'} />
                <Typography variant="caption" color="text.secondary">
                  Plan left: {planRemaining ?? 0} of {summary.entitlements.monthlyLeadLimit}. Wallet: {walletCredits}.
                </Typography>
              </Stack>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" fontWeight={900}>Onboarding progress</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {summary.onboarding.stepsCompleted} of {summary.onboarding.totalSteps} setup steps complete
            </Typography>
            <Stack spacing={1}>
              {onboardingSteps.map(([label, done]) => (
                <Box key={label} display="flex" justifyContent="space-between">
                  <Typography variant="body2">{label}</Typography>
                  <Chip label={done ? 'Done' : 'Pending'} color={done ? 'success' : 'default'} size="small" variant={done ? 'filled' : 'outlined'} />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="h6" fontWeight={900}>Recent unlocks</Typography>
              <Button component={RouterLink} to="/activity" size="small">View activity</Button>
            </Box>
            <Stack spacing={1.5}>
              {summary.recentUnlocks.length === 0 && (
                <Typography color="text.secondary">No unlocks yet. Open the rides feed to find your first ride.</Typography>
              )}
              {summary.recentUnlocks.map((item) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.5, bgcolor: '#fbfcff' }}>
                  <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Box>
                      <Typography fontWeight={800}>{item.ride.from || 'Pickup'} to {item.ride.to || 'Drop'}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.ride.time || 'Flexible'} · {new Date(item.unlockedAt).toLocaleDateString()}</Typography>
                    </Box>
                    <Chip label={item.ride.status} size="small" />
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>Referral progress</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {summary.referral?.referralCount || 0} drivers joined from your link.
            </Typography>
            <Box my={2}>
              <Typography variant="h4" fontWeight={900}>{summary.referral?.bonusUnlocks || 0}</Typography>
              <Typography variant="caption" color="text.secondary">bonus unlocks earned</Typography>
            </Box>
            <Button component={RouterLink} to="/referrals" variant="outlined">Share referral link</Button>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default DriverHome;
